import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { APP_CONFIG, UAU_CONFIG } from '../config/constants';
import { AuthContextManager } from '../context/AuthContext';

// ===============================
// TIPOS PARA EL SISTEMA DE RESPONSE WRAPPING
// ===============================

export interface WrappedSuccessResponse<T> {
  valid: true;
  data: T;
}

export interface WrappedErrorResponse {
  valid: false;
  error_type: string;
  message: string;
  original_status: number;
}

export type WrappedResponse<T> = WrappedSuccessResponse<T> | WrappedErrorResponse;

// ===============================
// TIPOS PARA RESPUESTAS DEL BACKEND
// ===============================

export interface CreateUpdateResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface DeleteResponse {
  success: boolean;
  message: string;
  deleted_id?: string | number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

// ===============================
// CONFIGURACIÓN BASE
// ===============================

const baseConfig: AxiosRequestConfig = {
  baseURL: APP_CONFIG.api_base_url,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
};

// ===============================
// INSTANCIA DE AXIOS
// ===============================

export const apiClient: AxiosInstance = axios.create(baseConfig);

// ===============================
// CONTROL DE SESIÓN SIMPLIFICADO
// ===============================

let isSessionExpired = false;
let cancelTokenSource: any = null;

// Inicializar cancel token source
if (axios.CancelToken) {
  cancelTokenSource = axios.CancelToken.source();
}

// Función para resetear estado (usada en login exitoso)
export const resetSessionState = () => {
  isSessionExpired = false;
  cancelTokenSource = axios.CancelToken?.source();
};

// ===============================
// INTERCEPTOR REQUEST - SIMPLE
// ===============================

apiClient.interceptors.request.use(
  async (config) => {
    // Agregar cancel token a todas las requests
    if (cancelTokenSource && !isSessionExpired) {
      config.cancelToken = cancelTokenSource.token;
    }

    // ⚠️ IMPORTANTE: Si el data es FormData, remover Content-Type default
    // Para que axios/navegador establezcan multipart/form-data con boundary correcto
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Agregar token si existe (pero NO en login)
    if (!config.url?.includes('/auth/login')) {
      const token = localStorage.getItem(UAU_CONFIG.STORAGE_KEYS.TOKEN);
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Agregar header X-Empresa-ID
    try {
      const empresaActivaStr = localStorage.getItem(UAU_CONFIG.STORAGE_KEYS.EMPRESA_ACTIVA);
      if (empresaActivaStr && config.headers && !config.url?.includes('/auth/')) {
        const empresaActiva = JSON.parse(empresaActivaStr);
        if (empresaActiva?.id) {
          config.headers['X-Empresa-ID'] = empresaActiva.id.toString();
        }
      }
    } catch (error) {
      // Silent - no bloquear request
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ===============================
// INTERCEPTOR RESPONSE - CON POST-WRAPPING UNWRAPPER
// ===============================

apiClient.interceptors.response.use(
  (response) => {
    // PASO 1: Desenvolver respuesta del backend si está envuelta
    if (response.data && typeof response.data === 'object' && 'valid' in response.data) {
      const wrappedData = response.data as WrappedResponse<any>;

      if (wrappedData.valid === true) {
        // Respuesta exitosa: extraer data original
        response.data = wrappedData.data;
        return response;
      } else {
        // Respuesta de error: recrear error HTTP original
        // ⭐ Extraer mensaje: puede ser string o objeto {message, error_code}
        let messageText: string = 'Error al procesar solicitud';

        if (typeof wrappedData.message === 'string') {
          messageText = wrappedData.message;
        } else if (typeof wrappedData.message === 'object' && wrappedData.message) {
          // Intentar extraer mensaje del objeto
          // Puede venir como: {message: "...", error_code: "..."}
          if (wrappedData.message.message) {
            messageText = String(wrappedData.message.message);
          } else if (wrappedData.message.msg) {
            messageText = String(wrappedData.message.msg);
          } else if (wrappedData.message.detail) {
            messageText = String(wrappedData.message.detail);
          } else {
            // Último recurso: stringificar el objeto completo para que toastHelper lo parsee
            messageText = JSON.stringify(wrappedData.message);
          }
        }

        const error: any = new Error(messageText);
        error.response = {
          status: wrappedData.original_status,
          data: { detail: messageText },
          headers: response.headers,
          config: response.config
        };
        error.config = response.config;
        error.request = response.request;
        error.isAxiosError = true;
        error.name = 'AxiosError';

        // ⭐ USAR WRAPPER COMO FUENTE ÚNICA DE VERDAD
        // El wrapper contiene: error_type, original_status, message
        error.error_type = wrappedData.error_type;
        error.original_status = wrappedData.original_status;
        error.isSilentError = wrappedData.error_type === 'unauthorized';  // 401 es esperado

        // CASO: 401 en endpoint NO de auth → sesión expirada
        const isAuthEndpoint = response.config?.url?.includes('/auth/');

        if (wrappedData.original_status === 401 && !isAuthEndpoint && !isSessionExpired) {
          isSessionExpired = true;

          // Cancelar todas las requests pendientes
          if (cancelTokenSource) {
            cancelTokenSource.cancel('Session expired - token invalid from wrapped response');
          }

          // ⭐ MARCAR ERROR COMO "ESPERADO"
          error.isSessionExpired = true;

          // Redirigir con delay
          setTimeout(() => {
            AuthContextManager.handleSessionExpired();
          }, 100);
        }

        return Promise.reject(error);
      }
    }

    // Si no está envuelta, devolver respuesta normal
    return response;
  },
  (error) => {
    // CASO 0: Requests canceladas - no mostrar errores
    if (axios.isCancel && axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // CASO 1: Errores de red (Network Error, CORS)
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      const isAuthEndpoint = error.config?.url?.includes('/auth/');

      if (!isAuthEndpoint) {
        if (!isSessionExpired) {
          // Primer error - activar cancelación y redirección
          isSessionExpired = true;

          // Cancelar todas las requests pendientes
          if (cancelTokenSource) {
            cancelTokenSource.cancel('Session expired - token invalid');
          }

          // Redirigir después de un pequeño delay
          setTimeout(() => {
            AuthContextManager.handleSessionExpired();
          }, 100);
        }
      }

      return Promise.reject(error);
    }

    // CASO 2: Errores HTTP sin wrapper (responses que no vienen del backend wrapper)
    // NOTA: Auth endpoints ahora se envuelven en CampingResponseMiddleware, así que esto es para fallback
    const isAuthEndpoint = error.config?.url?.includes('/auth/');

    // Si es error de autenticación esperado, marcar como silencioso
    if (error.response?.status === 401 && isAuthEndpoint) {
      error.isSilentError = true;
      // Fallback: Si no vino wrapped, intentar extraer mensaje del response
      // El mensaje puede ser string o objeto {message, error_code}
      let messageText = error.response?.data?.message || error.response?.data?.detail || error.message;

      // Si es objeto, extraer el campo message
      if (typeof messageText === 'object' && messageText?.message) {
        messageText = String(messageText.message);
      } else if (typeof messageText !== 'string') {
        messageText = 'Usuario o contraseña incorrectos';
      }

      error.message = messageText;
    }

    // Si es 401 fuera de /auth/, es sesión expirada
    if (error.response?.status === 401 && !isAuthEndpoint && !isSessionExpired) {
      isSessionExpired = true;

      // Cancelar todas las requests pendientes
      if (cancelTokenSource) {
        cancelTokenSource.cancel('Session expired - 401 unauthorized');
      }

      // Redirigir
      setTimeout(() => {
        AuthContextManager.handleSessionExpired();
      }, 100);
    }

    return Promise.reject(error);
  }
);

// ===============================
// CLIENTE API SIMPLIFICADO
// ===============================

export class ApiClient {
  /**
   * GET request básico
   */
  static async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.get(url, config);
    return response.data;
  }

  /**
   * POST request básico
   */
  static async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<CreateUpdateResponse<T>> {
    const response = await apiClient.post(url, data, config);
    return response.data;
  }

  /**
   * PUT request básico
   */
  static async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<CreateUpdateResponse<T>> {
    const response = await apiClient.put(url, data, config);
    return response.data;
  }

  /**
   * DELETE request básico
   */
  static async delete(url: string, config?: AxiosRequestConfig): Promise<DeleteResponse> {
    const response = await apiClient.delete(url, config);
    return response.data;
  }

  /**
   * PATCH request básico
   */
  static async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<CreateUpdateResponse<T>> {
    const response = await apiClient.patch(url, data, config);
    return response.data;
  }

  /**
   * Upload de archivos
   */
  static async upload<T>(url: string, file: File, additionalData?: any, fieldName: string = 'file', config?: AxiosRequestConfig): Promise<T> {
    const formData = new FormData();
    formData.append(fieldName, file);

    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    const uploadConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...config
    };

    const response = await apiClient.post(url, formData, uploadConfig);

    return response.data;
  }

  /**
   * Download de archivos
   */
  static async download(url: string, filename?: string): Promise<void> {
    const response = await apiClient.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // ===============================
  // MÉTODOS ESPECIALIZADOS
  // ===============================

  static async getList<T>(url: string, config?: AxiosRequestConfig): Promise<T[]> {
    return this.get<T[]>(url, config);
  }

  static async getPaginated<T>(url: string, config?: AxiosRequestConfig): Promise<PaginatedResponse<T>> {
    return this.get<PaginatedResponse<T>>(url, config);
  }

  static async getOne<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.get<T>(url, config);
  }

  // ===============================
  // UTILIDADES
  // ===============================

  static getBaseURL(): string {
    return baseConfig.baseURL || APP_CONFIG.api_base_url;
  }

  static getAuthToken(): string | null {
    return localStorage.getItem(UAU_CONFIG.STORAGE_KEYS.TOKEN);
  }

  static getEmpresaId(): string | null {
    try {
      const empresaActivaStr = localStorage.getItem(UAU_CONFIG.STORAGE_KEYS.EMPRESA_ACTIVA);
      if (empresaActivaStr) {
        const empresaActiva = JSON.parse(empresaActivaStr);
        return empresaActiva?.id?.toString() || null;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  static resetSession(): void {
    resetSessionState();
  }
}

export default apiClient;
